import React, { useState } from "react";
import Modal from "../../../component/ui/modal";
import TextInput from "../../../component/element/forms/TextInput";
import {
  ICreateNotification,
  ICreateNotificationError,
} from "../../../store/types/notification";
import { create_notification } from "../../../services/apis/notification";
import { useAppDispatch, useAppSelector } from "../../../store";
import RichTextEditor from "../../../component/element/forms/RichTextEditor";
import { CustomCheckbox } from "../../../component/CustomCheckBox";
import { Bell } from "lucide-react";

type IEmailNotification = {
  isOpen: boolean;
  isClose: () => void;
  onSuccess?: () => void;
};

const EmailNotification: React.FC<IEmailNotification> = ({
  isOpen,
  isClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.notification.loading);
  const [data, setData] = useState<ICreateNotification>({
    users: [],
    subject: "",
    message: "",
    mail: false,
    notification: false,
    service_request_id: "",
  });

  const [error, setError] = useState<ICreateNotificationError>({
    users: "",
    subject: "",
    message: "",
    mail: "",
    notification: "",
    service_request_id: "",
  });

  // Track the current input value separately
  const [recipientInput, setRecipientInput] = useState<string>("");

  const onValidate = () => {
    let hasError = false;
    setError({
      users: "",
      subject: "",
      message: "",
      mail: "",
      notification: "",
      service_request_id: "",
    });

    if (data.users.length === 0) {
      hasError = true;
      setError((prev) => ({ ...prev, users: "warning" }));
    }

    if (!data.subject.trim()) {
      hasError = true;
      setError((prev) => ({ ...prev, subject: "warning" }));
    }

    if (!data.message.trim()) {
      hasError = true;
      setError((prev) => ({ ...prev, message: "warning" }));
    }

    return !hasError;
  };

  const handleReceipientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRecipientInput(value);

    // Parse emails whenever input changes
    const emails = value
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0 && email.includes("@"));

    setData((prev) => ({ ...prev, users: emails }));
    setError((prev) => ({ ...prev, users: "" }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    const validate = onValidate();
    if (!validate) {
      return;
    }

    const submissionData = {
      ...data,
      // Remove service_request_id if it's empty
      ...(data.service_request_id === "" && { service_request_id: undefined }),
    };

    const response = await create_notification(submissionData, dispatch);
    if (response?.success) {
      setData({
        users: [],
        subject: "",
        message: "",
        mail: false,
        notification: false,
        service_request_id: "",
      });
      setRecipientInput("");
      onSuccess?.();
      isClose();
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setData({
      users: [],
      subject: "",
      message: "",
      mail: false,
      notification: false,
      service_request_id: "",
    });
    setRecipientInput("");
    isClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary-100/50 grid place-content-center mr-1">
            <Bell className="flex items-center size-4 text-primary-500" />
          </div>
          Send Message
        </div>
      }
      open={isOpen}
      onClose={handleClose}
      onOkClick={handleSubmit}
      isLoading={loading}
      variant="medium"
    >
      <div className="grid grid-cols-2 gap-4 mb-2">
        <CustomCheckbox
          id="mail"
          label="Mail"
          onChange={(checked: boolean) =>
            setData((prev) => ({ ...prev, mail: checked }))
          }
        />
        <CustomCheckbox
          id="notification"
          label="Notification"
          onChange={(checked: boolean) =>
            setData((prev) => ({ ...prev, notification: checked }))
          }
        />
      </div>
      <div className="grid gap-2">
        <TextInput
          label="Recipient"
          name="recipient"
          onChange={handleReceipientChange}
          placeholder="Email(s) separated with comma. e.g user@email.com, admin@email.com ..."
          type="string"
          value={recipientInput}
          status={error.users}
        />

        <TextInput
          label="Subject"
          name="subject"
          onChange={handleInputChange}
          placeholder="Enter email subject"
          type="text"
          value={data.subject}
          status={error.subject}
        />

        <div className="mt-2">
          <RichTextEditor
            label="Message"
            value={data.message}
            onChange={(value: string) =>
              setData((prev) => ({
                ...prev,
                message: value,
              }))
            }
          />
        </div>

        {/* Show parsed emails for better UX */}
        {data.users.length > 0 && (
          <div className="mt-2">
            <p className="text-sm font-medium text-gray-700 mb-1">
              Recipients ({data.users.length}):
            </p>
            <div className="flex flex-wrap gap-1">
              {data.users.map((email, index) => (
                <span
                  key={index}
                  className="bg-primary-200/50 text-primary-800 text-xs px-2 py-1 rounded"
                >
                  {email}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EmailNotification;
